import { isAfter, isBefore, isEqual, parse } from "date-fns";
import { useEffect, useState } from "react";

/**
 * Custom hook for filtering transactions.
 *
 * @param {Array} transactions - Raw transactions from API
 * @param {string} searchText - Search input for paysureRef
 * @param {boolean} checked - Checkbox toggle for status filter
 * @param {string} selectedStatus - Selected transaction status
 * @param {boolean} dateChecked - Checkbox toggle for date filtering
 * @param {Date|null} startDate - Selected start date
 * @param {Date|null} endDate - Selected end date
 */
export const useTransactionFilters = ({
  transactions,
  searchText,
  checked,
  selectedStatus,
  dateChecked,
  startDate,
  endDate,
}) => {
  const [filteredTransactions, setFilteredTransactions] = useState([]);

  // ------------------------------------------------------
  // Helper: safely parse the transaction date without crash
  // ------------------------------------------------------
  const safeParseDate = (value) => {
    if (!value || typeof value !== "string") return null;

    try {
      const parsed = parse(
        value,
        "yyyy-MM-dd HH:mm:ss.SSSSSS",
        new Date()
      );
      return isNaN(parsed.getTime()) ? null : parsed;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    let result = transactions;

    // ------------------------------------------
    // 🔍 SEARCH FILTER (paysureRef)
    // ------------------------------------------
    if (searchText.trim() !== "") {
      result = result.filter((t) =>
        String(t.paysureRef || "")
          .toLowerCase()
          .includes(searchText.toLowerCase())
      );
    }

    // ------------------------------------------
    // ✔ STATUS FILTER (checkbox + selected status)
    // ------------------------------------------
    if (checked && selectedStatus !== "") {
      // result = result.filter((t) => t.status === selectedStatus);
      result = result.filter((t) => {
        if (selectedStatus === "SUCCESS") {
          return t.status === "SUCCESS" || t.status === "SUCCESSFUL";
        } else if (selectedStatus ==="PENDING") {
          return t.status === "PENDING" || t.status === "Pending"
        }else {
          return t.status === selectedStatus;
        }
      });
    }

    // ------------------------------------------
    // 📅 DATE FILTER (start → end)
    // ------------------------------------------
    if (dateChecked) {
      // Normalize selected start date to midnight
      const start = startDate
        ? new Date(
            startDate.getFullYear(),
            startDate.getMonth(),
            startDate.getDate()
          )
        : null;

      // Normalize selected end date to midnight
      const end = endDate
        ? new Date(
            endDate.getFullYear(),
            endDate.getMonth(),
            endDate.getDate()
          )
        : null;

      result = result.filter((t) => {
        const parsed = safeParseDate(t.transactionDate);
        if (!parsed) return false; // skip invalid date rows

        // Normalize transaction date (remove time)
        const txDate = new Date(
          parsed.getFullYear(),
          parsed.getMonth(),
          parsed.getDate()
        );

        const afterStart =
          start ? isAfter(txDate, start) || isEqual(txDate, start) : true;

        const beforeEnd =
          end ? isBefore(txDate, end) || isEqual(txDate, end) : true;

        return afterStart && beforeEnd;
      });
    }

    setFilteredTransactions(result);
  }, [
    transactions,
    searchText,
    checked,
    selectedStatus,
    dateChecked,
    startDate,
    endDate,
  ]);

  return filteredTransactions;
};
